import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {assertAndContinue} from 'Asserts';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {FONT_PROXIMA_14} from 'styles/FontConstants';
import {smallSpacingLeft, smallSpacingRight} from 'styles/Utilities';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Pane from 'ui-library/pane/Pane';

const FEATURE_CONTOUR_PLOTS_DESCRIPTION =
  'This chart shows the relationship between a pair of parameters and the output. The points represent the top 10 recommendations from our model. Use the left and right arrows to browse relationships between other pairs of parameters.';
// Keep in sync with services/machine-learning/src/parameter_optimization/constants.py
const FEATURE_FILENAME_SEPARATOR = '!*!';
const FILENAME_SUFFIX = '.html';

export interface FeatureContourPlotsBlockProps {
  contourPlotFiles: Array<GetAnalysisScratchFile>;
}

export default function FeatureContourPlotsBlock({
  contourPlotFiles,
}: FeatureContourPlotsBlockProps): JSX.Element {
  const [fileIndex, setFileIndex] = useState(0);
  const filteredContourPlotFiles = contourPlotFiles.filter(
    (contourPlotFile) => contourPlotFile.externalFileId != null
  );
  assertAndContinue(
    contourPlotFiles.length === filteredContourPlotFiles.length,
    'One or more contour plot files has not been finalized'
  );
  const fileToDisplay = filteredContourPlotFiles[fileIndex];

  const featureNames = fileToDisplay.filename
    .slice(0, -1 * FILENAME_SUFFIX.length)
    .split(FEATURE_FILENAME_SEPARATOR)
    .map((featureName) => decodeURIComponent(featureName));
  assertAndContinue(
    featureNames.length === 2,
    `Expected feature contour plot filenames to be of the format {feature_name_1}${FEATURE_FILENAME_SEPARATOR}{feature_name_2}.`,
    {filename: fileToDisplay.filename}
  );

  return (
    <div className={css(styles.block)}>
      <Pane
        description={FEATURE_CONTOUR_PLOTS_DESCRIPTION}
        title={`${featureNames[0]} vs. ${featureNames[1]}`}
        toolbar={
          <>
            {fileIndex === 0 ? (
              <BenchlingIcon icon="chevron-left" className="faded" />
            ) : (
              <BenchlingIcon
                icon="chevron-left"
                className="u-pointer"
                onClick={() => {
                  setFileIndex(fileIndex - 1);
                }}
              />
            )}
            <span className={css(styles.pager)}>
              {/* Show fileIndex + 1 as users expect indexes to start at 1 and go to length of files. */}
              {fileIndex + 1} of {filteredContourPlotFiles.length}
            </span>
            {fileIndex === filteredContourPlotFiles.length - 1 ? (
              <BenchlingIcon icon="chevron-right" className="faded" />
            ) : (
              <BenchlingIcon
                icon="chevron-right"
                className="u-pointer"
                onClick={() => {
                  setFileIndex(fileIndex + 1);
                }}
              />
            )}
          </>
        }
      >
        <SignedExtFileURLProxy
          addHtmlContentTypeHeader={true}
          externalFileId={fileToDisplay.externalFileId!}
          url={fileToDisplay.downloadUrl!}
          renderWithSignedURL={(signedURL) => {
            if (signedURL === undefined) {
              return <CenteredHorizontalThrobber />;
            } else if (signedURL == null) {
              assertAndContinue(false, 'Got inaccessible file within an analysis', {
                externalFileId: fileToDisplay.externalFileId,
              });
              return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading file." />;
            } else {
              return (
                // oxlint-disable-next-line iframe-has-title
                <iframe
                  className={css(styles.iframe)}
                  sandbox="allow-scripts"
                  src={signedURL}
                  title={fileToDisplay.filename}
                />
              );
            }
          }}
          owningEntityApiId={fileToDisplay.id}
        />
      </Pane>
    </div>
  );
}

const BLOCK_HEIGHT = 610;
const BLOCK_WIDTH = 550;

const styles = StyleSheet.create({
  block: {
    height: BLOCK_HEIGHT,
    width: BLOCK_WIDTH,
  },
  pager: {
    ...FONT_PROXIMA_14,
    ...smallSpacingLeft,
    ...smallSpacingRight,
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
});
